/* Tarifas de Fueguino: las escalas de la carta de TransAmerica y el cotizador.
 *
 * ── DE DÓNDE SALEN LOS NÚMEROS ─────────────────────────────────────────────
 *
 * De la carta de incremento que TransAmerica le manda al cliente (la primera
 * que cargamos: JR TRANS S.R.L., vigencia 01/06/2026). Cada ruta tiene una
 * escala por tramo de kilos, una tarifa por metro cúbico, el "corte de carta
 * de porte" (un fijo por guía) y una bonificación. Los precios NO incluyen IVA.
 *
 * ── LA REGLA DE CÁLCULO ES PROVISORIA ──────────────────────────────────────
 *
 * `cotizar()` implementa una regla RAZONABLE pero SIN CONFIRMAR por Matías: el
 * primer tramo es un mínimo fijo, los demás son $/kg, y el flete es el MAYOR
 * entre lo que da por peso y lo que da por volumen (m³ × tarifa). Después se
 * aplica la bonificación y se suma el corte de carta de porte. En la pantalla
 * esto va marcado como borrador: cuando Matías confirme cómo cotiza de verdad,
 * se ajusta acá y los tests fijan la regla nueva. La ESCALA (los números) sí es
 * fiel a la carta; lo provisorio es cómo se combinan.
 */

/* Cada ruta: tramos de peso (hasta kg → precio), tarifa por m³, y de dónde salió.
 * El primer tramo de cada ruta es un MÍNIMO FIJO (no $/kg); el resto es $/kg. */
export const TARIFAS = {
  cliente: "JR TRANS S.R.L.",
  emisor: "TransAmerica Transportes S.A.",
  vigencia: "2026-06-01",
  corteCartaPorte: 14623,
  bonificacion: 0.05, // 5% sobre la tarifa
  sinIva: true,
  condicionPago: "20 días fecha de factura, con remitos conformados",
  rutas: [
    {
      id: "gallegos",
      nombre: "Capital Federal → Río Gallegos",
      tarifaM3: 259696,
      tramos: [
        { hasta: 25, precio: 29879, fijo: true },
        { hasta: 100, precio: 1592 },
        { hasta: 250, precio: 1457 },
        { hasta: 500, precio: 1161 },
        { hasta: 1000, precio: 999 },
        { hasta: 2000, precio: 906 },
        { hasta: Infinity, precio: 811 },
      ],
    },
    {
      id: "grande",
      nombre: "Capital Federal → Río Grande",
      tarifaM3: 331330,
      tramos: [
        { hasta: 20, precio: 53402, fijo: true },
        { hasta: 100, precio: 2456 },
        { hasta: 250, precio: 2253 },
        { hasta: 500, precio: 1741 },
        { hasta: 1000, precio: 1093 },
        { hasta: 2000, precio: 981 },
        { hasta: Infinity, precio: 887 },
      ],
    },
    {
      id: "ushuaia",
      nombre: "Capital Federal → Ushuaia",
      tarifaM3: 348051,
      tramos: [
        { hasta: 25, precio: 63038, fijo: true },
        { hasta: 50, precio: 3050 },
        { hasta: 100, precio: 2942 },
        { hasta: 250, precio: 2659 },
        { hasta: 500, precio: 1957 },
        { hasta: 1000, precio: 1255 },
        { hasta: 2000, precio: 1141 },
        { hasta: Infinity, precio: 1028 },
      ],
    },
  ],
};

export function rutaPorId(id) {
  return TARIFAS.rutas.find((r) => r.id === id) || null;
}

/* El tramo que le toca a un peso: el primero cuyo `hasta` lo cubre. */
export function tramoDe(ruta, kg) {
  return ruta.tramos.find((t) => kg <= t.hasta) || ruta.tramos[ruta.tramos.length - 1];
}

/* Cotiza una guía. REGLA PROVISORIA (ver cabecera):
 *   - por peso: si cae en el primer tramo (fijo) → ese mínimo; si no → kg × $/kg.
 *   - por volumen: m³ × tarifaM3.
 *   - flete = el mayor de los dos.
 *   - total = flete − bonificación + corte de carta de porte. (Sin IVA.)
 * Devuelve el desglose para que se vea de dónde sale cada peso. */
export function cotizar(ruta, kg, m3) {
  if (!ruta) return null;
  const peso = Number(kg) || 0;
  const vol = Number(m3) || 0;

  const tramo = tramoDe(ruta, peso);
  const porPeso = tramo.fijo ? tramo.precio : Math.round(peso * tramo.precio);
  const porVolumen = Math.round(vol * ruta.tarifaM3);

  const flete = Math.max(porPeso, porVolumen);
  const base = porPeso >= porVolumen ? "peso" : "volumen";
  const bonificacion = Math.round(flete * TARIFAS.bonificacion);
  const total = flete - bonificacion + TARIFAS.corteCartaPorte;

  return {
    ruta: ruta.nombre,
    kg: peso,
    m3: vol,
    tramo,
    porPeso,
    porVolumen,
    base, // cuál mandó: "peso" o "volumen"
    flete,
    bonificacion,
    corteCartaPorte: TARIFAS.corteCartaPorte,
    total,
    sinIva: TARIFAS.sinIva,
  };
}
